import React from 'react'

import {keyBy} from 'es-toolkit'
import {orderBy, some} from 'es-toolkit/compat'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {openSimpleContentModal} from '../../redux/features/modal'

import download, {downloadBlob} from '../../../../commons/src/services/download'
import {
  formatDateTime,
  formatDeliveryTime,
  formatInfillQuoteType,
  formatModelDimensions,
  formatPrice,
  formatScaleFactor,
  formatToleranceQuoteType,
} from '../../libs/formatter'
import {
  getProductionOption,
  getProductionTime,
  sanitizeHistory,
  scaleModelDimensions,
} from '../../libs/order'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'

import downloadIcon from '../../../../commons/src/assets/icons/download-box.svg'
import fileNoteIcon from '../../../../commons/src/assets/icons/file-note.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import CompactModelList from '../../../../commons/src/components/compact-model-list'
import CompactModelListItem from '../../../../commons/src/components/compact-model-list/CompactModelListItem'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import TruncatedLabel from '../../../../commons/src/components/truncated-label'
import ModelImage from '../../components/model-image'
import OrderStatusBadge from '../../components/order-status-badge'
import OrderTag from '../../components/order-tag'
import Sidebar from '../../components/sidebar'

import {BackendModelAttachment, ModelId, Order, OrderId} from '../../order-types'

import ProductionDetailsModal from './production-details-modal-content'

type Props = {
  order: Order
  onClose: () => void
}

const OrderDetailsAsidePartial = ({order, onClose}: Props) => {
  const dispatch = useAppDispatch()
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const printingMethods = useAppSelector(state => state.core.printingMethods)
  const api = isAdminMode ? internalApi : partnerApi

  const [isDownloadingAll, setIsDownloadingAll] = React.useState(false)

  const [orderHistory, modelsById] = React.useMemo(() => {
    const modelsKeyedById = keyBy(order.models, model => model.modelId)

    return [sanitizeHistory(order), modelsKeyedById]
  }, [order])

  const handleModelDownload = (modelIds: ModelId[]) => {
    Promise.all(
      modelIds.map(modelId =>
        api.getModelDownload(order.orderId, modelId).then(response => response.url)
      )
    ).then(urls => download(...urls))
  }

  const handleModelDownloadAll = () => {
    setIsDownloadingAll(true)
    partnerApi.getModelDownloadAll(order.orderId).then(result => {
      downloadBlob(result, `models_order#${order.orderNumber}`).finally(() =>
        setIsDownloadingAll(false)
      )
    })
  }

  const renderNote = (note: string) => (
    <TruncatedLabel
      size="s"
      minor
      hidePostfix
      postfix={
        <LinkButton
          onClick={() => {
            dispatch(
              openSimpleContentModal({title: 'Note', content: <Paragraph>{note}</Paragraph>})
            )
          }}
        >
          show
        </LinkButton>
      }
    >
      <Icon source={fileNoteIcon} className="u-margin-right-xs" />
      {`"${note}"`}
    </TruncatedLabel>
  )

  const renderProductionDetails = (
    orderId: OrderId,
    modelId: ModelId,
    note: string = '',
    attachments: BackendModelAttachment[] = []
  ) => {
    const showProductionDetailsModal = () => {
      dispatch(
        openSimpleContentModal({
          title: 'Production details',
          content: (
            <ProductionDetailsModal
              note={note}
              attachments={attachments}
              orderId={orderId}
              modelId={modelId}
            />
          ),
        })
      )
    }

    const attachmentCount = attachments?.length ?? 0
    const hasProductionDetails = note || !!attachmentCount

    return (
      hasProductionDetails && (
        <Stack space="60">
          <Paragraph strong className="u-no-margin-bottom">
            Production details
          </Paragraph>
          <TruncatedLabel
            title={note}
            postfix={
              <>
                {note && <>&quot;</>}
                {attachmentCount
                  ? note
                    ? ` (${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''})`
                    : `${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`
                  : null}{' '}
                <LinkButton onClick={showProductionDetailsModal}>show</LinkButton>
              </>
            }
          >
            {note && <>&quot;{note}</>}
          </TruncatedLabel>
        </Stack>
      )
    )
  }

  return (
    <Sidebar
      onClose={() => onClose()}
      title={
        <ActionBar space="80">
          <Headline className="u-no-margin" label="Order Details" />
        </ActionBar>
      }
    >
      {order?.note && renderNote(order.note)}
      <ActionBar space="80" alignItems="bottom">
        <Headline label="Customer" size="s" className="u-no-margin-bottom" />
        <Paragraph size="s" className="u-no-margin">
          {order &&
            `${order.customer.firstName} ${order.customer.lastName}${order.customer.companyName ? ` (${order.customer.companyName})` : ''}`}
        </Paragraph>
      </ActionBar>
      <SplitLayout
        left={
          <ActionBar space="80" alignItems="bottom">
            <Headline label="Production" size="s" className="u-no-margin-bottom" />
            <Paragraph size="s" className="u-no-margin">
              {order ? formatDeliveryTime(getProductionTime(order.quotes)) : ''}
            </Paragraph>
          </ActionBar>
        }
        right={
          <Paragraph size="s" className="u-no-margin">
            {order
              ? formatPrice(order.amounts.cartItems.netPrice, order.amounts.cartItems.currency, 2)
              : ''}
          </Paragraph>
        }
      />
      {order.additionalCosts && order.additionalCosts.length > 0 && (
        <Stack>
          <Headline
            label={`Additional Cost${(order.additionalCosts?.length || 0) > 1 ? 's' : ''}`}
            size="s"
            className="u-no-margin-bottom"
          />
          <Stack>
            {orderBy(order.additionalCosts, item => item.created, 'desc').map(additionalCost => (
              <SplitLayout
                growLeft
                key={additionalCost.created}
                left={
                  <Stack space="60">
                    <ActionBar alignItems="bottom">
                      <Paragraph size="s" className="u-no-margin">
                        {formatDateTime(additionalCost.created)}
                      </Paragraph>
                      <Paragraph size="s" minor className="u-no-margin-bottom">
                        {additionalCost.status.paid ? 'Paid' : 'Awaiting Payment'}
                      </Paragraph>
                    </ActionBar>
                    <TruncatedLabel
                      size="s"
                      hidePostfix
                      postfix={
                        <LinkButton
                          onClick={() => {
                            dispatch(
                              openSimpleContentModal({
                                title: 'Description',
                                content: <Paragraph>{additionalCost.description}</Paragraph>,
                              })
                            )
                          }}
                        >
                          show
                        </LinkButton>
                      }
                    >
                      {`"${additionalCost.description}"`}
                    </TruncatedLabel>
                  </Stack>
                }
                right={
                  <Paragraph size="s" className="u-no-margin">
                    {order
                      ? formatPrice(
                          additionalCost.amounts.netPrice,
                          additionalCost.amounts.currency,
                          2
                        )
                      : ''}
                  </Paragraph>
                }
              />
            ))}
          </Stack>
        </Stack>
      )}
      <SplitLayout
        left={
          <ActionBar space="80" alignItems="bottom">
            <Headline label="Shipping" size="s" className="u-no-margin-bottom" />
            <Paragraph size="s" className="u-no-margin">
              {order && order.shipping.deliveryTime
                ? formatDeliveryTime(order.shipping.deliveryTime)
                : ''}
              {order ? ` (${order.shipping.name})` : ''}
              {order.shipping.carrier ? ` via ${order.shipping.carrier}` : ''}
            </Paragraph>
            {order?.shipping.type === 'express' && <OrderTag type="express" />}
          </ActionBar>
        }
        right={
          <Paragraph size="s" className="u-no-margin">
            {order
              ? formatPrice(order.amounts.shipping.netPrice, order.amounts.shipping.currency, 2)
              : ''}
          </Paragraph>
        }
      />
      <Stack>
        <ActionBar space="80" alignItems="bottom">
          <Headline
            label="Models"
            addendum={order ? `(${order.quotes.length})` : ''}
            size="s"
            className="u-no-margin-bottom"
          />
          {!!order && (
            <>
              {!isDownloadingAll && !isAdminMode && (
                <LinkButton
                  icon={<Icon source={downloadIcon} />}
                  iconAlign="right"
                  onClick={handleModelDownloadAll}
                  disabled={isDownloadingAll}
                >
                  Download all models{' '}
                  {some(order.models, model => !!model.attachments && model.attachments.length > 0)
                    ? '(incl. attachments)'
                    : ''}
                </LinkButton>
              )}
              {isDownloadingAll && (
                <>
                  <Paragraph size="s" minor className="u-no-margin-bottom">
                    Downloading…
                  </Paragraph>
                  <LoadingIndicator />
                </>
              )}
            </>
          )}
        </ActionBar>
        <CompactModelList>
          {!!order &&
            !!modelsById &&
            order.quotes.map(quote => {
              const model = modelsById[quote.modelId]
              const scaledModelDimensions = scaleModelDimensions(model.dimensions, quote.scale)
              const productionOption = getProductionOption(quote)
              const infill = quote.types && formatInfillQuoteType(quote.types)
              const tolerance = quote.types && formatToleranceQuoteType(quote.types, true)
              const printingMethod =
                quote.printingMethodId && printingMethods[quote.printingMethodId]
                  ? printingMethods[quote.printingMethodId].name
                  : null
              const cncAnalysis = model.cncAnalysis

              return (
                <CompactModelListItem
                  key={quote.quoteId}
                  image={
                    <ModelImage
                      source={model.thumbnailUrl}
                      size="m"
                      alt={`Preview image of model ${model.fileName}`}
                    />
                  }
                  headline={
                    <ActionBar noWrap space="90">
                      <ActionBar noWrap space="70">
                        <strong>{quote.quantity}</strong>
                        <strong>x</strong>
                      </ActionBar>
                      <TruncatedLabel
                        postfix={
                          <LinkButton
                            icon={<Icon source={downloadIcon} />}
                            onClick={() => handleModelDownload([model.modelId])}
                          />
                        }
                      >
                        <Link onClick={() => handleModelDownload([model.modelId])}>
                          <strong>{model.fileName}</strong>
                        </Link>
                      </TruncatedLabel>
                    </ActionBar>
                  }
                  aside={
                    <Paragraph size="s" className="u-no-margin">
                      {formatPrice(quote.amounts.netPrice, quote.amounts.currency, 2)}
                    </Paragraph>
                  }
                >
                  <Stack space="60">
                    <ActionBar space="70">
                      {formatModelDimensions(scaledModelDimensions, model.fileUnit)}{' '}
                      {quote.scale !== 1 && (
                        <>
                          <OrderTag type="needsScaling" />
                          <Paragraph className="u-no-margin-bottom" size="xs" minor>
                            (scaled by {formatScaleFactor(quote.scale)})
                          </Paragraph>
                        </>
                      )}
                    </ActionBar>
                    <ActionBar space="80">
                      {quote.materialName}
                      {infill && (
                        <>
                          <OrderTag type="infill" /> {infill}
                        </>
                      )}
                      {tolerance && (
                        <>
                          <OrderTag type="tolerance" /> {tolerance}
                        </>
                      )}
                      {productionOption && <OrderTag type={productionOption} />}
                    </ActionBar>
                    {printingMethod && (
                      <>
                        <ActionBar space="80">Manufacturing process: {printingMethod}</ActionBar>
                        {printingMethod === 'CNC' && cncAnalysis && (
                          <>
                            <ActionBar space="80">
                              Axis tier: {cncAnalysis.axisRequirement || 'all'}
                            </ActionBar>
                            <ActionBar space="80">Setup count: {cncAnalysis.setupCount}</ActionBar>
                          </>
                        )}
                      </>
                    )}
                    {(quote.note || (model.attachments ?? []).length > 0) &&
                      renderProductionDetails(
                        order.orderId,
                        model.modelId,
                        quote.note,
                        model.attachments
                      )}
                  </Stack>
                </CompactModelListItem>
              )
            })}
        </CompactModelList>
      </Stack>
      <Stack space="80">
        <Headline label="History" size="s" className="u-no-margin-bottom" />
        {orderHistory.map(historyItem => (
          <ActionBar key={historyItem.date}>
            <OrderStatusBadge type={historyItem.status} />
            <Paragraph size="s" minor className="u-no-margin-bottom u-margin-left-s">
              {formatDateTime(historyItem.date)}
            </Paragraph>
          </ActionBar>
        ))}
      </Stack>
    </Sidebar>
  )
}

export default OrderDetailsAsidePartial
